/**
 * Sync orchestrator (spec 08) — framework-free so the future RN app reuses it.
 * Push all dirty → pull since cursor, paging until drained. Serialized with a
 * simple mutex; re-entrant calls coalesce into one trailing run.
 *
 * Storage and transport are injected: the web app backs `SyncStore` with IDB
 * and `SyncApi` with fetch; RN swaps both without touching this logic.
 */

export type SyncEntity = 'boat' | 'route' | 'track' | 'settings';

export interface LocalChange {
  entity: SyncEntity;
  id: string;
  updatedAt: number;
  deleted: boolean;
  payload?: Record<string, unknown>;
}

export interface ServerChange {
  entity: SyncEntity;
  id: string;
  seq: number;
  deleted: boolean;
  updatedAt: number;
  payload: Record<string, unknown> | null;
}

export interface PushResult {
  id: string;
  applied: boolean;
  seq: number;
}

export interface SyncStore {
  collectDirty(): Promise<LocalChange[]>;
  /** Clear the dirty flag on a successfully pushed row. */
  markSynced(entity: SyncEntity, id: string): Promise<void>;
  /** Apply an incoming server change (LWW guard lives in the adapter). */
  applyServer(change: ServerChange): Promise<void>;
  getCursor(): Promise<number>;
  setCursor(seq: number): Promise<void>;
}

export interface SyncApi {
  push(changes: LocalChange[]): Promise<{ results: PushResult[] }>;
  pull(since: number, limit: number): Promise<{ changes: ServerChange[]; nextSince: number; more: boolean }>;
}

const PULL_LIMIT = 500;
const PUSH_BATCH = 500;

export class SyncClient {
  private running = false;
  private queued = false;

  constructor(
    private readonly store: SyncStore,
    private readonly api: SyncApi,
  ) {}

  /** Run one full cycle; coalesces concurrent calls into a trailing run. */
  async sync(): Promise<void> {
    if (this.running) {
      this.queued = true;
      return;
    }
    this.running = true;
    try {
      do {
        this.queued = false;
        await this.pushPhase();
        await this.pullPhase();
      } while (this.queued);
    } finally {
      this.running = false;
    }
  }

  private async pushPhase(): Promise<void> {
    const dirty = await this.store.collectDirty();
    for (let i = 0; i < dirty.length; i += PUSH_BATCH) {
      const batch = dirty.slice(i, i + PUSH_BATCH);
      const { results } = await this.api.push(batch);
      for (const r of results) {
        if (!r.applied) continue; // rejected → pull overwrites with server copy
        const change = batch.find((c) => c.id === r.id);
        if (change) await this.store.markSynced(change.entity, r.id);
      }
    }
  }

  private async pullPhase(): Promise<void> {
    let since = await this.store.getCursor();
    for (;;) {
      const { changes, nextSince, more } = await this.api.pull(since, PULL_LIMIT);
      for (const change of changes) await this.store.applyServer(change);
      if (nextSince > since) {
        since = nextSince;
        await this.store.setCursor(since);
      }
      if (!more) break;
    }
  }
}
